import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { ACESFilmicToneMapping } from "three";

import {
  setUserLanguage,
  UserPreferenceKeys,
} from "../userPreferences/userPreferencesSlice";

import { API_SERVER_DOMAIN } from "@/constants";
import {
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  ColorThemes,
  DEFAULT_DARK_THEME,
  DEFAULT_LABELS_THEME,
  DEFAULT_THEME,
  HOVER_COLOR,
  HOVER_COLOR_DARK,
  LOCALSTORAGE_PREFIX,
  MeasurementSystems,
  type ReducedMotionTypes,
  SUPPORTED_LANGUAGES,
} from "@/features/app/constants";
import { safeLocalStorage } from "@/features/utils/utils";
import type { RootState } from "../../app/store";
import { ROUTE_DOT_SIZE_PX } from "../directions/constants";
import { RouteType } from "../directions/types";
import {
  getInitialLanguage,
  getInitialValue,
} from "../userPreferences/initialValues";
import {
  type DotNotationKeys,
  genericGetProperty,
  genericSetProperty,
  type NestedType,
} from "../utils/genericity";
import {
  BorderThemes,
  CartographyToShow,
  type ConfigState,
  DEFAULT_SHARE_PROFILE,
  FontSizeEnum,
  type FontSizeItem,
  type InitialViewState,
  MAP_CONTROL_ITEMS,
  MapAttributionsTypes,
  PerformanceMode,
  PrefetchMode,
  TTSEngine,
  UserSettingsPanelItem,
} from "./types";

const ACCURACY_TH = 6.0;
const JUMP_TH = 4.0;
const WALKED_TH = 7.0;
const NO_HAS_BEARING_TH = 3;
export const TTS_RATE_DEFAULT = 0.5;

const initialState: ConfigState = {
  actions: {
    actions: [],
  },
  auth: {
    api_key: import.meta.env.VITE_APIKEY,
    domain: API_SERVER_DOMAIN,
    jwt: null,
    production_api_key: null,
  },
  camera: {
    allowCameraAutoFitToRoute: true,
    lockToBuilding: true,
    maxCurrentBuildingZoom: null,
    maxZoom: null,
    maxZoomToCenter: null,
    minCurrentBuildingZoom: null,
    minZoom: null,
    minZoomToCenter: null,
    paddingHorizontal: null,
    paddingVertical: null,
  },
  directions: {
    defaultRouteType: getInitialValue<RouteType>(
      UserPreferenceKeys.DEFAULT_ROUTE_TYPE,
      RouteType.CHOOSE_SHORTEST,
    ),
    distanceToDiscriminateLookToTurn: 5,
    excludedTags: getInitialValue<string[]>(
      UserPreferenceKeys.EXCLUDED_TAGS,
      [],
    ),
    includedTags: [],
    longRouteWarning: {
      categoriesIds: [],
      distanceThreshold: null,
      maxDistanceToNearPois: 300,
      timeThreshold: null,
    },
    radiusPoiIndication: 1,
    slightTurnThreshold: 30,
    thresholdPoiDistances: 500,
    turnBackThreshold: 120,
    turnThreshold: 60,
  },
  favoritePois: {
    iconOutlineColor: "#ffffff",
  },
  feature_flags: {
    allow_reselecting_route_target: false,
    ar_integration: false,
    browser_positioning: false,
    browser_positioning_distance: 5000,
    custom_ui: null,
    directions_autoplay_steps_timer: 0,
    favorites: true,
    feedback_form: false,
    find_my_car_active: false,
    flat_earth: false,
    kiosk_mode: false,
    share_location: false,
    simplified_navigation_ui: false,
    speak_indications: false,
    ticket_selector: false,
    user_settings_button: true,
  },
  flight_information: {
    category_id: null,
    enabled: false,
    polling_interval_seconds: 300,
  },
  i18n: {
    language: getInitialLanguage(),
    measurementSystem: MeasurementSystems.metric,
    selectableLanguages: [...SUPPORTED_LANGUAGES],
  },
  informativeMessages: {
    showRouteDelayWarningToReachTheFlight: false,
  },
  internal: {
    appName: "Situm Map Viewer",
    autoResetView: {
      enabled: false,
      idleTime: 1,
    },
    crawlersAllowIndex: true,
    dashboardEmbed: false,
    debug: false,
    deviceId: null,
    disableNavigationEvents: false,
    embed: false,
    injectSimulation: false,
    limitedRefresh: false,
    locationQualityThresholds: {
      accuracyThreshold: ACCURACY_TH,
      locationsWithoutBearingThreshold: NO_HAS_BEARING_TH,
      maxJumpAllowedThreshold: JUMP_TH,
      minimumWalkThreshold: WALKED_TH,
    },
    locationUpdateThresholds: {
      accuracy: 10,
      animationDuration: 700,
      bearing: 10,
      distance: 1,
      minUpdateIntervalMs: 1000,
    },
    performanceMode: PerformanceMode.low,
    prefetch: {
      enableTilePrefetch: false,
      maxTileZoom: 21,
      minTileZoom: 17,
    },
    prefetchData: PrefetchMode.MINIMAL,
    profile: false,
    refreshDate: {
      hour: null,
      repeat: false,
    },
    refreshRate: null,
    showGeofences: true,
    showModels: true,
    showPaths: true,
    showPois: true,
    simulator: false,
    tryRefreshOnFirstLoad: false,
    tts: {
      engine: TTSEngine.WEB,
      pitch: 1,
      rate: safeLocalStorage.getItem(LOCALSTORAGE_PREFIX + "internal.tts.rate")
        ? (JSON.parse(
            safeLocalStorage.getItem(
              LOCALSTORAGE_PREFIX + "internal.tts.rate",
            ) as string,
          ) as number)
        : TTS_RATE_DEFAULT,
      volume: 1,
    },
  },
  map: {
    basemap: {
      active: true,
      custom_style_high_contrast_dark_url: null,
      custom_style_url: null,
      darkMode: false,
      preset: null,
      upload_style: null,
    },
    cartographyToShow: [CartographyToShow.BASEMAP, CartographyToShow.GEOJSON],
    initialView: {
      bearing: null,
      building_id: null,
      fixedPoi: null,
      floor_id: null,
      latitude: null,
      longitude: null,
      pitch: null,
      zoom: null,
    },
    interactive: true,
    lights: {
      anchor: "center",
      fill: {
        color: 0xffffff,
        intensity: 1.6,
        positionX: -1.5,
        positionY: 2.8,
        positionZ: -6.5,
      },
      hemisphere: {
        groundColor: 0x3d3d3d,
        intensity: 2.2,
        skyColor: 0xe7f4ff,
      },
      main: {
        color: 0xffffff,
        intensity: 1.5,
        positionX: -5.1,
        positionY: -5.2,
        positionZ: -2,
      },
    },
    newFloorplanLayer: true,
    newIconTextLayers: false,
    newIMDFLayer: true,
    newLocationBehaviour: false,
    newLocationLayer: false,
    newModelsLayers: false,
    newPoiLayer: false,
    newRouteIndicationLayer: false,
    newRouteLabelLayer: false,
    renderer: {
      toneMapping: ACESFilmicToneMapping,
      toneMappingExposure: 1,
    },
    shadows: {
      bias: -0.0001,
      cameraFarFactor: 0,
      cameraFarMin: 25,
      enabled: false,
      lodMinZoom: 12,
      mapSize: 2048,
      normalBias: 0.05,
      planeOpacity: 0.4,
      radius: 2,
      receiverSize: 200,
    },

    // TODO: disable if current building has no tiles configured
    useTiles: false, //TODO: Maintain for compatibility until all profiles are migrated
  },
  navigation: {
    distanceToChangeIndicationThreshold: 8,
    distanceToFloorChangeThreshold: 15,
    // Let navigation request options without an initial value so they
    // can be omitted from the API message if they were not defined.
    distanceToGoalThreshold: 7,
    distanceToIgnoreIndication: 3,
    distanceToReadIndicationTheshold: 20,
    indicationsInterval: 0,
    outsideRouteThreshold: 8,
    outsideRouteThresholdOnIncorrectFloor: 30,
    roundIndicationsStep: 0,
    timeToFirstIndication: 0,
    timeToIgnoreUnexpectedFloorChanges: 120,
    timeToUpdateNextIndication: 5,
  },
  poi: {
    spacingFactor: 1,
    zoomLevelToShowText: 1,
  },
  share: {
    baseUrl: window.location.origin,
    profile: DEFAULT_SHARE_PROFILE,
  },
  styles: {
    fixedPoi: {
      backgroundColor: "#ff0000",
      borderColor: "#ff0000",
      fontSize: 20,
      outlineColor: "#ff0000",
      showIcon: true,
      showLabel: true,
      showName: true,
      textColor: "#ffffff",
      textOutlineColor: "#ffffff",
    },
    map: {
      labels: DEFAULT_LABELS_THEME,
      pois: {
        dots: {
          color: "#777777",
          enabled: true,
          threshold: 1.5,
        },
        fadeOffRoutePois: true,
        iconBackground: {
          color: "#000000",
          enabled: false,
          iconScaleFactor: 0.8,
          iconType: "CIRCLE",
          pinHeight: 0,
        },
        iconScale: 1,
        outline: {
          color_selected: "#98fb98",
        },
      },
    },
    map_font: null,
    path: {
      dots_gap: 0,
      dots_size: ROUTE_DOT_SIZE_PX,
      show_step_arrow: true,
      step_fitting_zoom: 20,
    },
    path_animation_color: "",
    path_arrow_color: "",
    path_color: "",
    path_unselected_color: "",
    theme: {
      border_theme: BorderThemes.default,
      colorTheme: ColorThemes.system,
      dark_mode: {
        background_color: DEFAULT_DARK_THEME.backgroundColor,
        brand_color: BRAND_COLOR_DARK,
        hover_color: HOVER_COLOR_DARK,
        icon_color: DEFAULT_DARK_THEME.iconColor,
        primary_color: DEFAULT_DARK_THEME.primaryColor,
        primary_font_color: DEFAULT_DARK_THEME.primaryFontColor,
        secondary_color: DEFAULT_DARK_THEME.secondaryColor,
        secondary_font_color: DEFAULT_DARK_THEME.secondaryFontColor,
      },
      fonts: [],
      light_mode: {
        background_color: DEFAULT_THEME.backgroundColor,
        brand_color: BRAND_COLOR,
        hover_color: HOVER_COLOR,
        icon_color: DEFAULT_THEME.iconColor,
        primary_color: DEFAULT_THEME.primaryColor,
        primary_font_color: DEFAULT_THEME.primaryFontColor,
        secondary_color: DEFAULT_THEME.secondaryColor,
        secondary_font_color: DEFAULT_THEME.secondaryFontColor,
      },
      positioning_accuracy_color: DEFAULT_THEME.position.accuracyColor,
      positioning_dot_color: DEFAULT_THEME.position.dotColor,
      positioning_icon_color: DEFAULT_THEME.position.iconColor,
      positioning_outdoor_dot_color: DEFAULT_THEME.position.outdoorDotColor,
      warning_color: DEFAULT_THEME.warningColor,
    },
  },
  ui: {
    fontSize: getInitialValue<FontSizeItem>(
      UserPreferenceKeys.FONT_SIZE,
      FontSizeEnum.MD as FontSizeItem,
    ),
    header_image: null,
    map_attribution: MapAttributionsTypes.situm,
    poi_category_filter_interface: "grid",
    poi_surname: "${poiSurname} - ${floorName}",
    poiCategoriesOrder: [],
    reducedMotion: getInitialValue<ReducedMotionTypes>(
      UserPreferenceKeys.REDUCED_MOTION,
      "user",
    ),
    rtl: false,
    search_results: {
      fuzzy_threshold: 0.6,
      search_by: "both",
      sort_by: "both",
    },
    user_settings_panel_items: Object.values(UserSettingsPanelItem),
    whiteLabel: true,
    widget_visibility: {
      building_selector: true,
      destination_reached_confirmation: true,
      directions_panel: true,
      directions_shortcut: false,
      exploration_control: true,
      floor_selector: true,
      kiosk_qr_code: true,
      language_control: false,
      location_control: true,
      lock_floor_selector: false,
      map_control: true,
      map_control_items: MAP_CONTROL_ITEMS,
      navigation_indications: true,
      navigation_info: true,
      poi_categories: true,
      poi_list: true,
      poi_names: true,
      poi_names_navigation: true,
      poi_panel: true,
      poi_subcategories: true,
      route_type_selector: true,
      routeinfo_tooltip: true,
      search_bar: true,
      share_buttons: true,
      top_search_bar: true,
      zoom_buttons_mobile: false,
    },
  },
};

type SetConfigPayload<T extends DotNotationKeys<ConfigState>> = {
  key: T;
  value: NestedType<ConfigState, T>;
};
// TODO Remove
const USER_SETTINGS_KEYS = [
  "i18n.language",
  "directions.defaultRouteType",
  "directions.excludedTags",
  "styles.theme.colorTheme",
  "ui.reducedMotion",
  "ui.fontSize",
  "internal.tts.rate",
] as NestedType<ConfigState, DotNotationKeys<ConfigState>>[];

// This reducer needs to be kept apart to be able to preserve genericity
const setConfigItemName = "config/setConfigItem";
export function setConfigItem<T extends DotNotationKeys<ConfigState>>(
  key: T,
  value: NestedType<ConfigState, T>,
): PayloadAction<SetConfigPayload<T>> {
  // Save on the localStorage only if it's a user setting
  if (key !== "i18n.language" && USER_SETTINGS_KEYS.includes(key)) {
    // TODO Remove
    safeLocalStorage.setItem(LOCALSTORAGE_PREFIX + key, value);
  }

  return {
    payload: { key, value },
    type: setConfigItemName,
  };
}
setConfigItem.type = setConfigItemName;

const setInitialConfigItemName = "config/setInitialConfigItem";

export function setInitialConfigItem<T extends DotNotationKeys<ConfigState>>(
  key: T,
  value: NestedType<ConfigState, T>,
): PayloadAction<SetConfigPayload<T>> {
  return {
    payload: { key, value },
    type: setConfigItemName,
  };
}

setInitialConfigItem.type = setInitialConfigItemName;

const configSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(setConfigItem, (state, action) => {
      genericSetProperty(state, action.payload.key, action.payload.value);
    });
    builder.addCase(setInitialConfigItem, (state, action) => {
      genericSetProperty(state, action.payload.key, action.payload.value);
    });
    builder.addCase(setUserLanguage, (state, action) => {
      state.i18n.language = action.payload;
    });
  },
  initialState,
  name: "config",
  reducers: {
    resetInitialView: (state: ConfigState) => {
      state.map.initialView = {
        bearing: null,
        building_id: null,
        fixedPoi: null,
        floor_id: null,
        latitude: null,
        longitude: null,
        pitch: null,
        zoom: null,
      };
    },
    setApiKey: (state: ConfigState, action: PayloadAction<string>) => {
      state.auth.api_key = action.payload;
    },
    setBearing: (state: ConfigState, action: PayloadAction<number>) => {
      state.map.initialView.bearing = action.payload;
    },
    setBuildingId: (state: ConfigState, action: PayloadAction<number>) => {
      state.map.initialView.building_id = action.payload;
    },
    setDebug: (state: ConfigState, action: PayloadAction<boolean>) => {
      state.internal.debug = action.payload;
    },
    setDeviceId: (state: ConfigState, action: PayloadAction<string>) => {
      state.internal.deviceId = action.payload;
    },
    setDomain: (state: ConfigState, action: PayloadAction<string>) => {
      state.auth.domain = action.payload;
    },
    setEmbed: (state: ConfigState, action: PayloadAction<boolean>) => {
      state.internal.embed = action.payload;
    },
    setFloorId: (state: ConfigState, action: PayloadAction<number>): void => {
      state.map.initialView.floor_id = action.payload;
    },
    setInitialView: (
      state: ConfigState,
      action: PayloadAction<InitialViewState>,
    ) => {
      state.map.initialView = action.payload;
    },
    setMapFont: (state, action: PayloadAction<string>) => {
      state.styles.map_font = action.payload;
    },
    setPathAnimationColor: (state, action: PayloadAction<string>) => {
      state.styles.path_animation_color = action.payload;
    },
    setPathColor: (state, action: PayloadAction<string>) => {
      state.styles.path_color = action.payload;
    },
    setPitch: (state: ConfigState, action: PayloadAction<number>) => {
      state.map.initialView.pitch = action.payload;
    },
    setZoom: (state: ConfigState, action: PayloadAction<number>) => {
      state.map.initialView.zoom = action.payload;
    },
  },
});

const configReducer = configSlice.reducer;

export default configReducer;

const selectConfig = (state: RootState) => state.config;

export const selectEmbed = createSelector(
  selectConfig,
  (config) => config.internal.embed,
);
export const selectDebug = createSelector(
  selectConfig,
  (config) => config.internal.debug,
);
export const selectWhiteLabel = createSelector(
  selectConfig,
  (config) => config.ui.whiteLabel,
);
export const selectLanguage = createSelector(
  selectConfig,
  (config) => config.i18n.language,
);

export const selectSearchBarVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.search_bar,
);
export const selectPoiCategoriesVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.poi_categories,
);
export const selectNavigationIndicationsVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.navigation_indications,
);
export const selectNavigationInfoVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.navigation_info,
);
export const selectDestinationReachedVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.destination_reached_confirmation,
);
export const selectMapControlVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.map_control,
);
export const selectExplorationControlVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.exploration_control,
);

export const selectLocationControlVisibility = createSelector(
  selectConfig,
  (config) => config.ui.widget_visibility.location_control,
);
export const selectHeaderImage = createSelector(
  selectConfig,
  (config) => config.ui.header_image,
);

export const selectSlightTurnThreshold = createSelector(
  selectConfig,
  (config) => config.directions.slightTurnThreshold,
);

export const selectConfiguration = createSelector(
  selectConfig,
  (config) => config,
);

export const selectConfigItem =
  <T extends DotNotationKeys<ConfigState>>(key: T) =>
  (state: RootState) =>
    genericGetProperty(state.config, key);

export const selectIsUserSettingsPanelItemVisible =
  (item: UserSettingsPanelItem) => (state: RootState) =>
    state.config.ui.user_settings_panel_items.includes(item);

export const defaultConfig = initialState;

export const {
  setApiKey,
  setEmbed,
  setDebug,
  setDeviceId,
  setDomain,
  setInitialView,
  resetInitialView,
  setZoom,
  setBearing,
  setPitch,
  setBuildingId,
  setFloorId,
  setPathColor,
  setPathAnimationColor,
  setMapFont,
} = configSlice.actions;
